import { useHydrateAtoms } from "jotai/utils";
import { Fragment, type ReactNode } from "react";
import { graphql, readInlineData } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { section as sectionData } from "shared/data/section";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { Comment } from "scmp-app/components/comment";
import { MostPopular } from "scmp-app/components/most-popular";
import { Multimedia } from "scmp-app/components/multimedia";
import type { PlusWidgetsSampleContentType } from "scmp-app/components/plus/plus-widget";
import { PostMagazineDiscoveryWidget } from "scmp-app/components/post-magazine/discovery-widget";
import { AsiaDiscoveryWidget } from "scmp-app/components/rhs-module/asia-discovery-widget";
import { ChinaFutureTechDiscoveryWidget } from "scmp-app/components/rhs-module/china-future-tech-discovery-widget";
import { ChinaScienceDiscoveryWidget } from "scmp-app/components/rhs-module/china-science-discovery-widget";
import { OpenQuestionsSeriesDiscoveryWidget } from "scmp-app/components/rhs-module/open-questions-series-discovery-widget";
import { ThisWeekInAsiaDiscoveryWidget } from "scmp-app/components/rhs-module/this-week-in-asia-discovery-widget";
import { AiArticleWidget } from "scmp-app/components/section/ai-article-widget";
import { useSectionContext } from "scmp-app/components/section/contexts";
import { TrendingTopic } from "scmp-app/components/trending-topic";
import type { hooksSectionWidgetQuery$key } from "scmp-app/queries/__generated__/hooksSectionWidgetQuery.graphql";

import { getDefaultSectionAdsConfigs } from "./ads-config";
import { defaultSectionAtom } from "./atoms";
import { getPlusFallbackWidget, getSectionWidgets } from "./helpers";
import {
  CommentContainer,
  FifthAds,
  FirstAds,
  FourthAds,
  LatestContainer,
  MostPopularContainer,
  MultimediaContainer,
  RHSModule1,
  RHSModule2,
  RHSModule3,
  RHSModule4,
  RHSModule5,
  RHSModule6,
  SecondAds,
  SixthAds,
  StyledHomeFocusArticle,
  StyledPlusWidget,
  ThirdAds,
  TrendingTopicContainer,
} from "./styles";
import type { PWAAdvertCampaignAppConfig, SectionWidget } from "./types";

const premiumNativeAdSections = [
  sectionData.asia.entityId,
  sectionData.thisWeekInAsia.entityId,
  sectionData.world.entityId,
  sectionData.business.entityId,
  sectionData.china.entityId,
  sectionData.economy.entityId,
  sectionData.hongKong.entityId,
  sectionData.lifestyle.entityId,
  sectionData.news.peopleAndCulture.entityId,
  sectionData.sport.entityId,
  sectionData.tech.entityId,
] as string[];

const reduceWidgetSections = [
  sectionData.thisWeekInAsia.entityId,
  sectionData.lifestyle.entityId,
  sectionData.world.entityId,
  sectionData.news.peopleAndCulture.entityId,
  sectionData.sport.entityId,
  sectionData.asia.entityId,
  sectionData.china.entityId,
  sectionData.tech.entityId,
] as string[];

export const useRenderAdsAndSectionWidgets = (
  reference: hooksSectionWidgetQuery$key,
  LHSModuleLength: number,
) => {
  const { advertisement: baseAdvertisement } = useSectionContext();
  const data = readInlineData(
    graphql`
      fragment hooksSectionWidgetQuery on Query
      @inline
      @argumentDefinitions(
        applicationId: { type: "String!" }
        commentHarrySectionIds: { type: "[String]" }
        commentQueueName: { type: "String!" }
        commentQueueLimit: { type: "Int!" }
        dailyPulseTypeUuId: { type: "String!" }
        focusQueueName: { type: "String!" }
        highlightQueueName: { type: "String!" }
        mostPopularQueueName: { type: "String!" }
        multimediaQueueName: { type: "String!" }
        plusNewsAgendaEndDate: { type: "Timestamp!" }
        plusNewsAgendaStartDate: { type: "Timestamp!" }
        plusNewsAgendaTypes: { type: "[String]!" }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
        sectionId: { type: "String!" }
        trendingTopicsQueueName: { type: "String!" }
        # Include widget features
        includeAsiaDiscoveryWidget: { type: "Boolean!" }
        includeChinaFutureTechDiscoveryWidget: { type: "Boolean!" }
        includeChinaScienceDiscoveryWidget: { type: "Boolean!" }
        includeComment: { type: "Boolean!" }
        includeFocusArticle: { type: "Boolean!" }
        includeMostPopular: { type: "Boolean!" }
        includeMultimedia: { type: "Boolean!" }
        includeOpenQuestionsSeriesDiscoveryWidget: { type: "Boolean!" }
        includePlus: { type: "Boolean!" }
        includeThisWeekInAsiaDiscovery: { type: "Boolean!" }
        includeTrendingTopics: { type: "Boolean!" }
      ) {
        advertConfig: appConfig(filter: { entityId: "scmp_pwa_advert_campaign" }) {
          json
        }
        section: section(filter: { entityId: $sectionId }) {
          entityId
          advertZone(version: 2)
        }
        ...thisWeekInAsiaDiscoveryWidgetQuery @include(if: $includeThisWeekInAsiaDiscovery)
        ...commentQueueQuery
          @include(if: $includeComment)
          @arguments(
            commentHarrySectionIds: $commentHarrySectionIds
            commentLimit: $commentQueueLimit
            commentQueueName: $commentQueueName
            scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
            withHarrysView: false
          )
        ...discoveryWidgetPostMagazineQueueQuery
        ...mostPopularQueueQuery
          @include(if: $includeMostPopular)
          @arguments(mostPopularQueueName: $mostPopularQueueName)
        ...multimediaQuery
          @include(if: $includeMultimedia)
          @arguments(multimediaQueueName: $multimediaQueueName)
        ...trendingTopicTopic
          @include(if: $includeTrendingTopics)
          @arguments(trendingTopicsQueueName: $trendingTopicsQueueName)
        ...plusWidgetQuery
          @include(if: $includePlus)
          @arguments(
            applicationId: $applicationId
            dailyPulseTypeUuId: $dailyPulseTypeUuId
            highlightQueueName: $highlightQueueName
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
        ...asiaDiscoveryWidgetQuery @include(if: $includeAsiaDiscoveryWidget)
        ...chinaScienceDiscoveryWidgetQuery @include(if: $includeChinaScienceDiscoveryWidget)
        ...chinaFutureTechDiscoveryWidgetQuery @include(if: $includeChinaFutureTechDiscoveryWidget)
        ...focusArticleHomeFocusArticleQuery
          @include(if: $includeFocusArticle)
          @arguments(focusQueueName: $focusQueueName)
        ...openQuestionsSeriesDiscoveryWidgetQuery
          @arguments(scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds)
          @include(if: $includeOpenQuestionsSeriesDiscoveryWidget)
      }
    `,
    reference,
  );

  const appConfig = (data?.advertConfig?.json ?? {}) as PWAAdvertCampaignAppConfig;

  const renderThisWeekInAsia = () => <ThisWeekInAsiaDiscoveryWidget reference={data} />;

  const renderComment = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "opinion",
        pgtype: "section",
      }}
    >
      <CommentContainer>
        <Comment reference={data} withMoreCommentButton={false} />
      </CommentContainer>
    </BaseLinkContextProvider>
  );
  const renderMultimedia = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "multimedia",
        pgtype: "section",
      }}
    >
      <MultimediaContainer>
        <Multimedia reference={data} />
      </MultimediaContainer>
    </BaseLinkContextProvider>
  );
  const renderMostPopular = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "most_popular",
        pgtype: "section",
      }}
    >
      <MostPopularContainer>
        <MostPopular reference={data} />
      </MostPopularContainer>
    </BaseLinkContextProvider>
  );
  const renderTrendingTopics = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "trending_topics",
        pgtype: "section",
      }}
    >
      <TrendingTopicContainer>
        <TrendingTopic reference={data} />
      </TrendingTopicContainer>
    </BaseLinkContextProvider>
  );
  const renderRecommendedForYou = () => {
    let sectionIds = baseAdvertisement.targeting.scsid ?? [];
    if (baseAdvertisement.targeting.scsid?.includes(sectionData.olympic.entityId)) {
      sectionIds = [sectionData.sport.entityId];
    }
    return <AiArticleWidget sectionIds={sectionIds} />;
  };

  const renderPlusWidget = () => {
    const sampleContentMapping: Record<string, PlusWidgetsSampleContentType> = {
      [sectionData.business.entityId]: sectionData.business.aiEngineName,
      [sectionData.china.entityId]: sectionData.china.aiEngineName,
      [sectionData.chinaFutureTech.entityId]: sectionData.chinaFutureTech.aiEngineName,
      [sectionData.economy.entityId]: sectionData.economy.aiEngineName,
      [sectionData.hongKong.entityId]: sectionData.hongKong.aiEngineName,
    };
    const augmentedSectionWidgetComponentMap: Record<SectionWidget, () => ReactNode> = {
      ...sectionWidgetComponentMap,
      "china-science-discovery": () => (
        <LatestContainer>
          <ChinaScienceDiscoveryWidget desktopItemCount={3} reference={data} />
        </LatestContainer>
      ),
    };
    return (
      <StyledPlusWidget
        fallbackWidget={
          augmentedSectionWidgetComponentMap[getPlusFallbackWidget(data.section.entityId)]
        }
        pageType="section"
        reference={data}
        sampleContent={sampleContentMapping[data.section.entityId] ?? null}
      />
    );
  };

  const renderPostMagazine = () => <PostMagazineDiscoveryWidget reference={data} />;
  const renderFocusArticle = () => <StyledHomeFocusArticle pageType="section" reference={data} />;
  const renderOpenQuestionsSeriesDiscoveryWidget = () => (
    <OpenQuestionsSeriesDiscoveryWidget reference={data} />
  );

  const renderChinaFutureTech = () => (
    <LatestContainer>
      <ChinaFutureTechDiscoveryWidget reference={data} />
    </LatestContainer>
  );

  const renderChinaScience = () => (
    <LatestContainer>
      <ChinaScienceDiscoveryWidget reference={data} />
    </LatestContainer>
  );

  const renderAsia = () => (
    <LatestContainer>
      <AsiaDiscoveryWidget reference={data} />
    </LatestContainer>
  );

  const rhsModuleComponents = [
    RHSModule1,
    RHSModule2,
    RHSModule3,
    RHSModule4,
    RHSModule5,
    RHSModule6,
  ];

  const sectionWidgets = getSectionWidgets(data.section.entityId);
  const sectionWidgetComponentMap: Record<SectionWidget, () => ReactNode> = {
    "asia-discovery": renderAsia,
    "china-future-tech-discovery": renderChinaFutureTech,
    "china-science-discovery": renderChinaScience,
    comment: renderComment,
    "focus-article": renderFocusArticle,
    "most-popular": renderMostPopular,
    multimedia: renderMultimedia,
    "open-questions-series-discovery": renderOpenQuestionsSeriesDiscoveryWidget,
    plus: renderPlusWidget,
    "post-magazine": renderPostMagazine,
    "recommended-for-you": renderRecommendedForYou,
    "this-week-in-asia-discovery": renderThisWeekInAsia,
    "trending-topics": renderTrendingTopics,
  };
  const rhsModule = sectionWidgets.map(widget => sectionWidgetComponentMap[widget]);

  const adSlotComponents = [SecondAds, ThirdAds, FourthAds, FifthAds, SixthAds];
  const adConfigs = getDefaultSectionAdsConfigs(
    data.section.entityId,
    data?.section?.advertZone ?? undefined,
  );
  const [[firstAdConfigDesktop, firstAdConfigTablet, firstAdConfigMobile], ...restSdConfigs] =
    adConfigs;

  let updatedRHSModule = rhsModule;
  let updatedDesktopAdsConfig = restSdConfigs;
  const restSdConfigsLength = restSdConfigs.length;

  if (LHSModuleLength / 1.8 < rhsModule.length) {
    const removeCount = rhsModule.length - Math.floor(LHSModuleLength / 1.8);
    updatedRHSModule = rhsModule.slice(0, -removeCount);

    switch (data.section.entityId) {
      case sectionData.comment.entityId:
      case sectionData.kPop.entityId:
      case sectionData.olympic.entityId:
      case sectionData.sport.entityId:
      case sectionData.tech.entityId:
        updatedDesktopAdsConfig = restSdConfigs.slice(0, restSdConfigsLength - 1);
        break;
      default:
        updatedDesktopAdsConfig = restSdConfigs.slice(0, restSdConfigsLength - 2);
        break;
    }
  }

  const render = () => (
    <>
      <FirstAds>
        {firstAdConfigDesktop && <AdSlot {...firstAdConfigDesktop} />}
        {firstAdConfigTablet && <AdSlot {...firstAdConfigTablet} />}
        <AdSlot {...firstAdConfigMobile} />
      </FirstAds>
      {/* temp using HK section layout for all */}
      {/* later should change back to 'data.section.entityId' */}
      {updatedRHSModule.map((rhsModule, index) => {
        const Wrapper = rhsModuleComponents[index];
        const AdSlotComponent = adSlotComponents[index];
        const [desktopAdsConfig] = updatedDesktopAdsConfig[index] ?? [];
        const [_, tabletAdsConfig, mobileAdsConfig] = restSdConfigs[index] ?? [];
        return (
          <Fragment key={index}>
            <Wrapper>{rhsModule()}</Wrapper>
            {(desktopAdsConfig || mobileAdsConfig) && (
              <AdSlotComponent>
                {desktopAdsConfig && <AdSlot {...desktopAdsConfig} />}
                {tabletAdsConfig && <AdSlot {...tabletAdsConfig} />}
                {mobileAdsConfig && <AdSlot {...mobileAdsConfig} />}
              </AdSlotComponent>
            )}
          </Fragment>
        );
      })}
    </>
  );

  // enable premium native ad for specific sections
  const sections = appConfig?.premiumNativeAd?.section;
  const sectionPremiumNativeAdEnable =
    (premiumNativeAdSections.includes(data.section.entityId) &&
      sections?.[data.section.entityId]?.enable) ??
    false;
  const reduceWidgetCount =
    sectionPremiumNativeAdEnable && reduceWidgetSections.includes(data.section.entityId);
  useHydrateAtoms([[defaultSectionAtom, { reduceWidgetCount, sectionPremiumNativeAdEnable }]]);

  return { render };
};
